import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  AccordionStyleConfigurations,
  ButtonAndInputStatesConfig,
  ButtonConfigBaseStyles,
  ButtonIconConfigurations,
  ButtonLoadingConfig,
  ButtonLoadingIndicatorType,
  ButtonSizeConfigurations,
  ButtonSizeOptions,
  ButtonSizeStyleDefaults,
  ButtonTypes,
  ColourGroupFields,
  CompanyInfo,
  ConfirmationDialogStyleConfigurations,
  CustomColourType,
  GlobalThemeConfiguration,
  InputBaseStyles,
  InputLabelConfig,
  NewColourPaletteType,
  OtpInputFieldSpacingConfig,
  TooltipStyleConfigurations,
  TypographyKeys,
  TypographyStyleConfigurations,
  accordionStyleDefaults,
  buttonBaseStyles,
  buttonColorDefaults,
  buttonIconConfigurationDefaults,
  defaultGlobalThemeTypographyConfig,
  filterPillsStyleDefaults,
  inputBaseDefaults,
  inputColorDefaults,
  inputLabelDefaults,
  ListStyleConfigurations,
  listStyleDefaults,
  paddingWithIconStyleDefaults,
  TableStyleConfigurations,
  TabsOrientationStyleConfigurations,
  TabsOrientationType,
  ThemeAssetsConfig,
  tableStyleDefaults,
  tabsOrientationStyleDefaults,
  tooltipStyleDefaults,
} from '@finxone-platform/shared/sys-config-types';
import { confirmationDialogStyleDefaults } from 'libs/shared/sys-config-types/src/global-themeing-utils/global-theming-confirmation-dialog.utils';
import { buildFoundationsFormGroup } from './global-theme-foundation-tokens-form.utils';
import { buildAppearanceFormGroup, buildModeOverridesFormGroup } from './global-theme-appearance-form.utils';
import { buildElevationFormGroup } from './global-theme-elevation-form.utils';
import { buildCustomGroupFormGroup } from './global-theme-palette-custom-groups-form.utils';
import { buildTypographyStyleFormGroup } from './global-theme-typography-custom-styles-form.utils';
import { orderRecord } from './global-theme-token-order.utils';

/** The palette groups a theme without a palette starts with. */
const DEFAULT_PALETTE_GROUPS: Record<string, ColourGroupFields> = {
  main: { primary: '', background: '', surface: '', borderColor: '' },
  border: { default: '' },
  states: { active: '', hover: '' },
  status: { info: '', success: '', warning: '', error: '' },
  typography: {
    onPrimary: '',
    onPrimaryMuted: '',
    onBackground: '',
    onBackgroundMuted: '',
    onSurface: '',
    onSurfaceMuted: '',
  },
};

/** A palette's colour groups: every keyed group, not the legacy customColors list. */
function groupsOf(palette: NewColourPaletteType): Record<string, ColourGroupFields> {
  return Object.fromEntries(
    Object.entries(palette).filter(
      ([groupName, group]) =>
        groupName !== 'customColors' && !!group && typeof group === 'object' && !Array.isArray(group),
    ),
  ) as Record<string, ColourGroupFields>;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalThemeFormFactoryService {
  constructor(private fb: FormBuilder) {}

  public globalThemeFormGroup: FormGroup;

  public generateGlobalThemeFormGroup(globalThemeObject: GlobalThemeConfiguration) {
    this.globalThemeFormGroup = this.buildGlobalThemeFormGroup(globalThemeObject);
  }

  /**
   * A new theme form, independent of the current one: every sub-group is created fresh,
   * so building (or failing to build) never touches the live form.
   */
  public buildGlobalThemeFormGroup(globalThemeObject: GlobalThemeConfiguration): FormGroup {
    const form = this.fb.group<any>({
      themeName: new FormControl(globalThemeObject.themeName),
      generalConfigurations: this.fb.group({
        companyInfo: this.buildCompanyInfoFormGroup(),
        palette: this.getPaletteFormGroup(
          globalThemeObject.generalConfigurations.palette,
          globalThemeObject.tokenOrder?.palette,
        ),
        fontFamily: '',
        secondaryFontFamily: '',
        extraCss: '',
        loaderCss: '',
        defaultSystemFont: false,
      }),
      buttons: this.buildButtonsFormGroup(),
      typography: this.getTypographyFormGroup(
        globalThemeObject.typography,
        globalThemeObject.tokenOrder?.typography,
      ),
      inputFields: this.buildInputFieldsFormGroup(),
      tooltipConfigurations: this.buildTooltipFormGroup(),
      confirmationDialogConfigurations: this.buildConfirmationDialogFormGroup(),
      tabsConfigurations: this.buildTabsFormGroup(),
      tableConfigurations: this.buildTableFormGroup(),
      listConfigurations: this.buildListFormGroup(),
      filterPillsConfigurations: this.buildFilterPillsFormGroup(),
      accordionConfigurations: this.buildAccordionFormGroup(),
      foundations: buildFoundationsFormGroup(
        globalThemeObject.foundations,
        globalThemeObject.tokenOrder?.foundations,
      ),
      // Edited as a whole by the border radius classes section.
      ...(globalThemeObject.radius && { radius: new FormControl(globalThemeObject.radius) }),
      textSkeleton: this.buildTextSkeletonsFormGroup(),
      // Only themes that already have an appearance get these controls, so one without it saves unchanged.
      ...(globalThemeObject.appearance && {
        appearance: buildAppearanceFormGroup(globalThemeObject.appearance),
        modeOverrides: buildModeOverridesFormGroup(globalThemeObject.modeOverrides),
      }),
      ...(globalThemeObject.elevation && { elevation: buildElevationFormGroup(globalThemeObject.elevation) }),
      // Sterling config is carried whole until it has an editor; absent fields stay absent.
      ...(globalThemeObject.components && { components: new FormControl(globalThemeObject.components) }),
      ...(globalThemeObject.rules && { rules: new FormControl(globalThemeObject.rules) }),
      assets: new FormControl<ThemeAssetsConfig>(globalThemeObject.assets ?? { logos: [] }),
    });
    // foundations is built above: its form rows don't share the saved keyed shape.
    const { foundations: _foundations, ...patch } = globalThemeObject;
    form.patchValue(patch);
    return form;
  }

  /**
   * Swaps every top-level control of the live form for `next`'s, keeping the root
   * instance so subscriptions to it and lookups through it stay valid. Emits one
   * valueChanges and marks the form dirty, like any other edit.
   */
  public replaceGlobalThemeControls(next: FormGroup): void {
    const form = this.globalThemeFormGroup;
    for (const key of Object.keys(form.controls)) {
      if (!(key in next.controls)) form.removeControl(key, { emitEvent: false });
    }
    for (const [key, control] of Object.entries(next.controls)) {
      form.setControl(key, control, { emitEvent: false });
    }
    form.updateValueAndValidity();
    form.markAsDirty();
  }

  private buildCompanyInfoFormGroup(): FormGroup {
    return this.fb.group<CompanyInfo>({
      name: '',
      legalEntityName: '',
      logo: '',
      logoTitle: '',
      logoFileName: '',
    });
  }

  /**
   * One FormGroup control per typography style. A stored typography config's styles are
   * taken as they are, so a set pasted in the Advanced tab can replace the default
   * styles; only a theme with no typography gets the default styles (TypographyKeys).
   * Styles follow the saved `order`.
   */
  private getTypographyFormGroup(
    typography?: GlobalThemeConfiguration['typography'],
    order?: string[],
  ): FormGroup {
    const styles: Record<string, Partial<TypographyStyleConfigurations> | undefined> =
      typography ?? defaultGlobalThemeTypographyConfig.typography;
    const controls: Record<string, FormGroup> = {};
    for (const [key, style] of Object.entries(styles)) {
      controls[key] = buildTypographyStyleFormGroup(style);
    }
    return new FormGroup(orderRecord(controls, order));
  }

  /**
   * A stored palette's groups are taken as they are, in its order: a default group it
   * lacks stays absent and one keeps only its own colours, so a palette pasted in the
   * Advanced tab can replace the defaults. Only a theme with no palette gets the default
   * groups, with blank colours. Colours within each group follow the saved `order`.
   */
  private getPaletteFormGroup(palette?: NewColourPaletteType, order?: Record<string, string[]>): FormGroup {
    const customColorArray: Array<CustomColourType> = palette?.customColors || [];
    const customColorsFormArray = new FormArray<FormGroup>([]);

    for (let i = 0; i < customColorArray.length; i++) {
      customColorsFormArray.push(
        new FormGroup({
          name: this.fb.control(customColorArray[i].name || ''),
          code: this.fb.control(customColorArray[i].code || ''),
        }),
      );
    }

    // Default and custom groups are built alike, as sibling FormGroups on the palette;
    // the legacy customColors array stays a separate list.
    const groupControls: Record<string, FormGroup> = {};
    for (const [groupName, colours] of Object.entries(palette ? groupsOf(palette) : DEFAULT_PALETTE_GROUPS)) {
      groupControls[groupName] = buildCustomGroupFormGroup(orderRecord(colours, order?.[groupName]));
    }

    return new FormGroup({ ...groupControls, customColors: customColorsFormArray });
  }

  private buildButtonsFormGroup(): FormGroup {
    return new FormGroup({
      [ButtonTypes.PRIMARY]: this.fb.group({
        idle: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        active: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        hover: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        loading: this.fb.group<ButtonLoadingConfig>(this.buttonLoadingDefaults()),
        baseStyles: this.fb.group<ButtonConfigBaseStyles>(buttonBaseStyles()),
        size: this.fb.group({
          [ButtonSizeOptions.SMALL]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
          [ButtonSizeOptions.LARGE]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
          [ButtonSizeOptions.FULL_WIDTH]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
        }),
        borderRadiusClass: this.newFormControl(''),
      }),
      [ButtonTypes.SECONDARY]: this.fb.group({
        idle: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        active: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        hover: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        loading: this.fb.group<ButtonLoadingConfig>(this.buttonLoadingDefaults()),
        baseStyles: this.fb.group<ButtonConfigBaseStyles>(buttonBaseStyles()),
        size: this.fb.group({
          [ButtonSizeOptions.SMALL]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
          [ButtonSizeOptions.LARGE]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
          [ButtonSizeOptions.FULL_WIDTH]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
        }),
        borderRadiusClass: this.newFormControl(''),
      }),
      [ButtonTypes.TERTIARY]: this.fb.group({
        idle: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        active: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        hover: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        loading: this.fb.group<ButtonLoadingConfig>(this.buttonLoadingDefaults()),
        baseStyles: this.fb.group<ButtonConfigBaseStyles>(buttonBaseStyles()),
        borderRadiusClass: this.newFormControl(''),
        size: this.fb.group({
          [ButtonSizeOptions.SMALL]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
          [ButtonSizeOptions.LARGE]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
          [ButtonSizeOptions.FULL_WIDTH]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
        }),
      }),
      [ButtonTypes.MENU]: this.fb.group({
        idle: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        active: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        hover: this.fb.group<Omit<ButtonAndInputStatesConfig, 'labelColor'>>(buttonColorDefaults()),
        loading: this.fb.group<ButtonLoadingConfig>(this.buttonLoadingDefaults()),
        baseStyles: this.fb.group<ButtonConfigBaseStyles>(buttonBaseStyles()),
        size: this.fb.group({
          [ButtonSizeOptions.SMALL]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
          [ButtonSizeOptions.LARGE]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
          [ButtonSizeOptions.FULL_WIDTH]: this.fb.group<ButtonSizeConfigurations>({
            ...ButtonSizeStyleDefaults(),
            ...{ paddingWithIcon: paddingWithIconStyleDefaults() },
          }),
        }),
        borderRadiusClass: this.newFormControl(''),
        iconConfigurations: this.fb.group<ButtonIconConfigurations>(buttonIconConfigurationDefaults()),
      }),
    });
  }

  private buildInputFieldsFormGroup(): FormGroup {
    return new FormGroup({
      idle: this.fb.group<ButtonAndInputStatesConfig>(inputColorDefaults()),
      active: this.fb.group<ButtonAndInputStatesConfig>(inputColorDefaults()),
      labelConfig: this.fb.group<InputLabelConfig>(inputLabelDefaults()),
      inputStyles: this.fb.group<InputBaseStyles>(inputBaseDefaults()),
      textStyleClass: this.newFormControl(''),
      borderRadiusClass: this.newFormControl(''),
      otpInputFieldSpacing: this.fb.group<OtpInputFieldSpacingConfig>({
        'margin-right': '',
      }),
      finxForm: this.fb.group({ gap: '5px' }),
    });
  }

  private buildTextSkeletonFormGroup(): { [key in TypographyKeys]: FormGroup } {
    const group = {} as { [key in TypographyKeys]: FormGroup };
    (Object.values(TypographyKeys) as TypographyKeys[]).forEach((key) => {
      group[key] = this.fb.group({
        'background-color': this.fb.control(''),
        'border-top-left-radius': this.fb.control(''),
        'border-top-right-radius': this.fb.control(''),
        'border-bottom-right-radius': this.fb.control(''),
        'border-bottom-left-radius': this.fb.control(''),
      });
    });
    return group;
  }
  private buildTextSkeletonsFormGroup(): FormGroup {
    return new FormGroup(this.buildTextSkeletonFormGroup());
  }
  private buildTooltipFormGroup(): FormGroup {
    return this.fb.group<TooltipStyleConfigurations>(tooltipStyleDefaults());
  }
  private buildTabsFormGroup(): FormGroup {
    return new FormGroup({
      [TabsOrientationType.HORIZONTAL]: this.fb.group<TabsOrientationStyleConfigurations>(
        tabsOrientationStyleDefaults(),
      ),
      [TabsOrientationType.VERTICAL]: this.fb.group<TabsOrientationStyleConfigurations>(
        tabsOrientationStyleDefaults(),
      ),
    });
  }
  private buildTableFormGroup(): FormGroup {
    return this.fb.group<TableStyleConfigurations>(tableStyleDefaults());
  }
  private buildListFormGroup(): FormGroup {
    return this.fb.group<ListStyleConfigurations>(listStyleDefaults());
  }

  private buildFilterPillsFormGroup(): FormGroup {
    return new FormGroup(
      Object.fromEntries(
        Object.entries(filterPillsStyleDefaults()).map(([key, value]) => [key, new FormControl(value)]),
      ),
    );
  }

  private buildConfirmationDialogFormGroup(): FormGroup {
    return this.fb.group<ConfirmationDialogStyleConfigurations>(confirmationDialogStyleDefaults());
  }
  private buildAccordionFormGroup(): FormGroup {
    return this.fb.group<AccordionStyleConfigurations>(accordionStyleDefaults());
  }

  private newFormControl(value = '', validators: any[] = [Validators.required]): FormControl<string> {
    return new FormControl(value, validators) as FormControl<string>;
  }

  private buttonLoadingDefaults(): ButtonLoadingConfig {
    return {
      indicatorType: ButtonLoadingIndicatorType.ANIMATION,
      loadingText: '',
    };
  }

  //Generic function to get nested form groups from parent
  public getNestedFormGroup(type: string): FormGroup {
    return this.globalThemeFormGroup.get(type) as FormGroup;
  }
}
